import { isMapId, mapInfo, MAPS } from "@69ly/maps";
import { BEST_OF, MATCH_LABELS, type BestOf, type DemoSource, type MatchDetail, type MatchLabel, type MatchPlayer, type MatchSummary } from "@69ly/shared";
import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { BackLink } from "../components/BackLink.tsx";
import { Icon } from "../components/Icon.tsx";
import { Select } from "../components/Select.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { useDeleteDemo, useDeleteMatch, useMatch, useMatches, useJoinDemo, useMatchNotes, useMergeMatch, useSeparateDemo, useSplitDemo, useUpdateMatch } from "../lib/demo-queries.ts";
import { errorText } from "../lib/errors.ts";
import { mapIconUrl } from "../lib/map-art.ts";
import { useMe } from "../lib/queries.ts";
import { FaceitMatchesView } from "./FaceitMatches.tsx";
import { NotesBrowser } from "./NotesList.tsx";

const EDIT_SOURCES: DemoSource[] = ["faceit", "premier", "scrim", "other"];
export const mapName = (map: string) => (isMapId(map) ? mapInfo(map).name : map);
const minutes = (s: number) => Math.max(1, Math.round(s / 60));
const dateInput = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() - new Date(iso).getTimezoneOffset() * 60_000).toISOString().slice(0, 10) : "");

function useDate() {
  const { i18n } = useTranslation();
  return (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" });
}

/** "Team", "Stack", "Solo", "Scouting", "Pro": who of the team played. */
export function LabelBadge({ label }: { label: MatchLabel }) {
  const { t } = useTranslation();
  return (
    <span className={`match-label is-${label}`} title={t(`matches.labelHelp.${label}`)}>
      {t(`matches.label.${label}`)}
    </span>
  );
}

/** WIN, LOSS or DRAW, in words (not colour alone). */
function Outcome({ outcome }: { outcome: MatchSummary["outcome"] }) {
  const { t } = useTranslation();
  if (!outcome) return null;
  return <span className={`match-outcome is-${outcome}`}>{t(`matches.outcome.${outcome}`)}</span>;
}

/** A map as a chip: icon, name, score from side A, ✓ or ✗ when we played. Opens the viewer. */
function MapChip({ match, m }: { match: MatchSummary; m: MatchSummary["maps"][number] }) {
  const { t } = useTranslation();
  const ours = match.outcome !== null;
  const mark = ours && m.winner ? (m.winner === "a" ? "✓" : "✗") : null;
  const label =
    t("matches.mapChip", { n: m.number, map: mapName(m.map), a: m.score[0], b: m.score[1] }) + (ours && m.winner ? `, ${t(m.winner === "a" ? "matches.outcome.win" : "matches.outcome.loss")}` : "");
  return (
    <Link to={`/demos/${m.demoId}`} className={`map-chip${ours && m.winner ? ` is-${m.winner === "a" ? "won" : "lost"}` : ""}`} aria-label={label}>
      {isMapId(m.map) && <img src={mapIconUrl(m.map)} alt="" width={22} height={22} />}
      <span>{mapName(m.map)}</span>
      <b className="mono">
        {m.score[0]}–{m.score[1]}
      </b>
      {mark && (
        <span className="map-chip-mark" aria-hidden="true">
          {mark}
        </span>
      )}
    </Link>
  );
}

/** Our players' faces, and how many randoms played with them. */
function Lineup({ match }: { match: MatchSummary }) {
  const { t } = useTranslation();
  if (match.members.length === 0) return null;
  return (
    <div className="match-lineup">
      <div className="match-faces" aria-hidden="true">
        {match.members.slice(0, 5).map((m) => (
          <Avatar key={m.id} name={m.name} url={m.avatarUrl} size={24} />
        ))}
      </div>
      <span className="small muted">
        {match.members.map((m) => m.name).join(", ")}
        {match.randoms > 0 && ` ${t("matches.randoms", { count: match.randoms })}`}
      </span>
    </div>
  );
}

function MatchMeta({ match }: { match: MatchSummary }) {
  const { t } = useTranslation();
  const date = useDate();
  return <p className="muted small match-meta">{[match.event, match.stage, match.playedAt ? date(match.playedAt) : null, t("matches.bestOf", { n: match.bestOf })].filter(Boolean).join(" · ")}</p>;
}

export function MatchCard({ match }: { match: MatchSummary }) {
  const { t } = useTranslation();
  return (
    <li className="card match-card">
      <div className="match-card-head">
        <Link to={`/matches/${match.id}`} state={{ from: "matches" }} className="match-card-title">
          {match.title}
        </Link>
        <LabelBadge label={match.label} />
        {!match.pro && <span className={`demo-source source-${match.source}`}>{t(`demos.source.${match.source}`)}</span>}
      </div>
      <MatchMeta match={match} />
      <div className="match-series">
        <span className={`match-side${match.result[0] > match.result[1] ? " is-ahead" : ""}`}>{match.sides[0]}</span>
        <b className="mono match-series-score" aria-label={t("matches.seriesAria", { a: match.result[0], b: match.result[1] })}>
          {match.result[0]} : {match.result[1]}
        </b>
        <span className={`match-side${match.result[1] > match.result[0] ? " is-ahead" : ""}`}>{match.sides[1]}</span>
        <Outcome outcome={match.outcome} />
      </div>
      <ul className="match-maps" aria-label={t("matches.maps")}>
        {match.maps.map((m) => (
          <li key={m.demoId}>
            <MapChip match={match} m={m} />
          </li>
        ))}
      </ul>
      <Lineup match={match} />
    </li>
  );
}

type LabelFilter = "all" | MatchLabel;
const FILTER_LABELS: readonly LabelFilter[] = ["all", ...MATCH_LABELS];

/** All the team's matches, and the Pro ones: grouped by match, filtered in the address. */
export function MatchesPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const { team } = useCurrentTeam();
  const isAdmin = useMe().data?.isAdmin ?? false;
  const wanted = (params.get("label") ?? (team ? "all" : "pro")) as LabelFilter;
  const label: LabelFilter = FILTER_LABELS.includes(wanted) ? (team || wanted === "pro" ? wanted : "pro") : "all";
  const pro = label === "pro";
  const teamList = useMatches(team?.id ?? null);
  const proList = useMatches("pro");
  const list = pro ? proList : teamList;
  const all = useMemo(() => list.data ?? [], [list.data]);
  const map = params.get("map");
  const result = params.get("result");
  const player = params.get("player");
  const event = params.get("event");
  const q = (params.get("q") ?? "").trim().toLowerCase();
  // The library, or the team's FACEIT matches.
  const view = team && params.get("view") === "faceit" ? "faceit" : "library";

  // From the address as it is now: two changes in a row must not undo each other.
  const set = (key: string, value: string | null) => {
    const n = new URLSearchParams(window.location.search);
    if (value === null || value === "") n.delete(key);
    else n.set(key, value);
    setParams(n, { replace: true });
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: teamList.data?.length ?? 0, pro: proList.data?.length ?? 0 };
    for (const m of teamList.data ?? []) c[m.label] = (c[m.label] ?? 0) + 1;
    return c;
  }, [teamList.data, proList.data]);
  const events = [...new Set(all.map((m) => m.event).filter((e): e is string => !!e))].sort();
  const members = [...new Map(all.flatMap((m) => m.members).map((m) => [m.id, m])).values()].sort((a, b) => a.name.localeCompare(b.name));
  const mapsHere = MAPS.filter((m) => all.some((x) => x.maps.some((g) => g.map === m.id)));

  const shown = all.filter(
    (m) =>
      (pro || label === "all" || m.label === label) &&
      (!map || m.maps.some((g) => g.map === map)) &&
      (!result || m.outcome === result) &&
      (!player || m.members.some((x) => x.id === player)) &&
      (!event || m.event === event) &&
      (!q || `${m.title} ${m.sides.join(" ")} ${m.event ?? ""}`.toLowerCase().includes(q)),
  );
  const filtered = !!(map || result || player || event || q);
  const canImport = pro ? isAdmin : !!team;
  const back = encodeURIComponent(location.pathname + location.search);
  const importLink = canImport ? (
    <Link to={`/demos/import?to=${pro ? "pro" : team!.id}&back=${back}`} className="btn btn-primary">
      <Icon name="upload" />
      {t("demos.importButton")}
    </Link>
  ) : null;

  return (
    <div className="page matches-page">
      <header className="page-head">
        <div>
          <h1 className="page-title">{t("matches.title")}</h1>
          <p className="muted">{pro ? t("demos.proHelp") : t("matches.help")}</p>
        </div>
        {importLink}
      </header>

      {team && (
        <div className="segmented match-views" role="group" aria-label={t("matches.view")}>
          {(["library", "faceit"] as const).map((v) => (
            <button key={v} type="button" className="seg" aria-pressed={view === v} onClick={() => set("view", v === "library" ? null : v)}>
              {t(`matches.views.${v}`)}
            </button>
          ))}
        </div>
      )}

      {view === "faceit" && team ? (
        <FaceitMatchesView teamId={team.id} />
      ) : (
        <>
          <div className="match-labels" role="group" aria-label={t("matches.whoPlayed")}>
            {FILTER_LABELS.filter((l) => team || l === "pro").map((l) => (
              <button
                key={l}
                type="button"
                className="match-filter"
                aria-pressed={label === l}
                title={l === "all" ? undefined : t(`matches.labelHelp.${l}`)}
                onClick={() => set("label", l === "all" ? null : l)}
              >
                {l === "all" ? t("matches.all") : t(`matches.label.${l}`)}
                <span className="match-filter-count">{counts[l] ?? 0}</span>
              </button>
            ))}
          </div>
          {!team && <p className="muted small">{t("matches.teamOnly")}</p>}

          <div className="match-filters">
            <Select
              label={t("matches.filters.map")}
              value={map ?? ""}
              placeholder={t("demos.allMaps")}
              groups={[
                { options: [{ value: "", label: t("demos.allMaps") }, ...mapsHere.map((m) => ({ value: m.id, label: m.name, icon: <img src={mapIconUrl(m.id)} alt="" width={18} height={18} /> }))] },
              ]}
              onChange={(v) => set("map", v)}
            />
            {!pro && label !== "scouting" && (
              <div className="segmented segmented-small" role="group" aria-label={t("matches.filters.result")}>
                {(["", "win", "loss"] as const).map((r) => (
                  <button key={r || "any"} type="button" className="seg" aria-pressed={(result ?? "") === r} onClick={() => set("result", r)}>
                    {r ? t(`matches.filters.${r}`) : t("matches.filters.anyResult")}
                  </button>
                ))}
              </div>
            )}
            {!pro && members.length > 0 && (
              <Select
                label={t("matches.filters.player")}
                value={player ?? ""}
                placeholder={t("matches.filters.anyPlayer")}
                groups={[{ options: [{ value: "", label: t("matches.filters.anyPlayer") }, ...members.map((m) => ({ value: m.id, label: m.name }))] }]}
                onChange={(v) => set("player", v)}
              />
            )}
            {events.length > 0 && (
              <Select
                label={t("matches.filters.event")}
                value={event ?? ""}
                placeholder={t("matches.filters.anyEvent")}
                groups={[{ options: [{ value: "", label: t("matches.filters.anyEvent") }, ...events.map((e) => ({ value: e, label: e }))] }]}
                onChange={(v) => set("event", v)}
              />
            )}
            <label className="match-search">
              <span className="visually-hidden">{t("matches.filters.search")}</span>
              <Icon name="search" />
              <input type="search" value={params.get("q") ?? ""} placeholder={t("matches.filters.search")} onChange={(e) => set("q", e.target.value)} />
            </label>
          </div>

          {list.isPending ? (
            <Loading />
          ) : list.error ? (
            <ErrorState message={errorText(t, list.error)} onRetry={() => void list.refetch()} />
          ) : all.length === 0 ? (
            <div className="card learn-empty">
              <h2 className="section-title">{pro ? t("demos.proEmpty") : t("matches.emptyTitle")}</h2>
              {!pro && <p className="muted">{t("matches.emptyHelp")}</p>}
              {importLink}
            </div>
          ) : shown.length === 0 ? (
            <div className="card learn-empty">
              <p className="muted">{t("matches.noMatch")}</p>
              {filtered && (
                <button type="button" className="btn btn-secondary" onClick={() => setParams(label === "all" ? {} : { label }, { replace: true })}>
                  {t("mapView.clearFilters")}
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="muted small" role="status">
                {t("matches.shown", { count: shown.length })}
              </p>
              <ul className="match-list">
                {shown.map((m) => (
                  <MatchCard key={m.id} match={m} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- one match

export function MatchPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const match = useMatch(id);
  if (match.isPending) return <Loading />;
  if (match.error) return <ErrorState message={errorText(t, match.error)} onRetry={() => void match.refetch()} />;
  return <MatchView match={match.data} />;
}

function MatchView({ match }: { match: MatchDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [editing, setEditing] = useState(false);
  const del = useDeleteMatch();
  const delMap = useDeleteDemo();
  const split = useSplitDemo();
  const join = useJoinDemo();
  const separate = useSeparateDemo();
  const merge = useMergeMatch(match.id);
  const library = useMatches(match.pro ? "pro" : match.teamId);
  const others = (library.data ?? []).filter((m) => m.id !== match.id);
  const [error, setError] = useState<string | null>(null);
  const listUrl = match.pro ? "/matches?label=pro" : "/matches";
  const scope = match.pro ? "pro" : match.teamId!;
  const fail = (e: unknown) => setError(errorText(t, e));

  return (
    <div className="page match-page">
      <BackLink fallback={listUrl} fallbackLabel={t("matches.title")} from="matches" />
      <header className="match-head">
        <div className="match-head-title">
          <h1 className="match-title">{match.title}</h1>
          <LabelBadge label={match.label} />
          {!match.pro && <span className={`demo-source source-${match.source}`}>{t(`demos.source.${match.source}`)}</span>}
        </div>
        <MatchMeta match={match} />
        <div className="match-series is-big">
          <span className={`match-side${match.result[0] > match.result[1] ? " is-ahead" : ""}`}>{match.sides[0]}</span>
          <b className="mono match-series-score" aria-label={t("matches.seriesAria", { a: match.result[0], b: match.result[1] })}>
            {match.result[0]} : {match.result[1]}
          </b>
          <span className={`match-side${match.result[1] > match.result[0] ? " is-ahead" : ""}`}>{match.sides[1]}</span>
          <Outcome outcome={match.outcome} />
        </div>
        <Lineup match={match} />
        <div className="row match-actions">
          {match.maps[0] && (
            <Link to={`/demos/${match.maps[0].demoId}`} className="btn btn-primary">
              <Icon name="play" />
              {t("matches.watch")}
            </Link>
          )}
          {match.canEdit && match.maps.length < 5 && (
            <Link to={`/demos/import?to=${scope}&match=${match.id}&back=${encodeURIComponent(location.pathname)}`} className="btn btn-secondary">
              <Icon name="plus" />
              {t("matches.addMap")}
            </Link>
          )}
          {match.canEdit && (
            <button type="button" className="btn btn-secondary" aria-expanded={editing} onClick={() => setEditing((e) => !e)}>
              <Icon name="pen" />
              {t("matches.edit")}
            </button>
          )}
          {match.canEdit && (
            <button
              type="button"
              className="btn btn-quiet btn-danger"
              disabled={del.isPending}
              onClick={() => {
                if (window.confirm(t("matches.confirmDelete", { title: match.title, count: match.maps.length }))) del.mutate(match.id, { onSuccess: () => void navigate(listUrl), onError: fail });
              }}
            >
              <Icon name="trash" />
              {t("matches.delete")}
            </button>
          )}
        </div>
        {error && <p className="field-error">{error}</p>}
      </header>

      {editing && <MatchEditForm match={match} onDone={() => setEditing(false)} />}

      <section className="card match-section" aria-labelledby="match-maps">
        <h2 id="match-maps" className="section-title">
          {t("matches.maps")}
        </h2>
        <ol className="match-map-rows">
          {match.maps.map((m, i) => (
            <li key={m.demoId} className="match-map-row">
              <span className="match-map-number mono" aria-hidden="true">
                {m.number}
              </span>
              {isMapId(m.map) && <img src={mapIconUrl(m.map)} alt="" width={36} height={36} />}
              <div className="match-map-main">
                <b>{mapName(m.map)}</b>
                <span className="muted small">
                  {t("demos.rounds", { count: m.rounds })} · {t("demos.minutes", { count: minutes(m.durationSeconds) })}
                  {m.parts.length > 0 && (
                    <>
                      {" · "}
                      <span className="match-map-parts" title={t("matches.relaunch.joinedHelp")}>
                        <Icon name="link" size={12} />
                        {t("matches.relaunch.joined", { count: m.parts.length + 1 })}
                      </span>
                    </>
                  )}
                </span>
              </div>
              <span className="mono match-map-score">
                {m.score[0]}–{m.score[1]}
              </span>
              {match.outcome !== null && m.winner && (
                <span className={`match-outcome is-${m.winner === "a" ? "win" : "loss"}`}>{t(m.winner === "a" ? "matches.outcome.win" : "matches.outcome.loss")}</span>
              )}
              <Link to={`/demos/${m.demoId}`} className="btn btn-secondary btn-small" aria-label={t("matches.watchMap", { n: m.number, map: mapName(m.map) })}>
                <Icon name="play" size={14} />
                {t("demos.watch")}
              </Link>
              {match.canEdit && (
                <Link
                  to={`/demos/import?to=${scope}&again=${m.demoId}&back=${encodeURIComponent(location.pathname)}`}
                  className="btn btn-quiet btn-small"
                  aria-label={t("matches.readAgainMap", { n: m.number, map: mapName(m.map) })}
                  title={t("demos.import.againHelp")}
                >
                  <Icon name="loop" size={14} />
                  {t("demos.import.again")}
                </Link>
              )}
              {match.canEdit && m.parts.length > 0 && (
                <button
                  type="button"
                  className="btn btn-quiet btn-small"
                  title={t("matches.relaunch.separateHelp")}
                  disabled={separate.isPending}
                  onClick={() => separate.mutate(m.demoId, { onError: fail })}
                >
                  {t("matches.relaunch.separate")}
                </button>
              )}
              {match.canEdit && m.parts.length === 0 && i > 0 && match.maps[i - 1].map === m.map && (
                <button
                  type="button"
                  className={`btn btn-small ${m.restored || match.maps[i - 1].restored ? "btn-secondary" : "btn-quiet"}`}
                  title={t(m.restored || match.maps[i - 1].restored ? "matches.relaunch.joinRestored" : "matches.relaunch.joinHelp")}
                  disabled={join.isPending}
                  onClick={() => join.mutate(m.demoId, { onError: fail })}
                >
                  <Icon name="link" size={14} />
                  {t("matches.relaunch.join", { n: m.number - 1 })}
                </button>
              )}
              {match.canEdit && match.maps.length > 1 && (
                <button
                  type="button"
                  className="btn btn-quiet btn-small"
                  title={t("matches.splitHelp")}
                  disabled={split.isPending}
                  onClick={() => split.mutate(m.demoId, { onSuccess: (s) => void navigate(`/matches/${s.id}`), onError: fail })}
                >
                  {t("matches.split")}
                </button>
              )}
              {match.canEdit && (
                <button
                  type="button"
                  className="btn btn-quiet btn-small btn-danger"
                  aria-label={t("matches.removeMap", { n: m.number, map: mapName(m.map) })}
                  title={t("matches.removeMap", { n: m.number, map: mapName(m.map) })}
                  disabled={delMap.isPending}
                  onClick={() => {
                    if (!window.confirm(t("matches.confirmRemoveMap", { map: mapName(m.map) }))) return;
                    delMap.mutate(m.demoId, { onSuccess: () => match.maps.length === 1 && void navigate(listUrl), onError: fail });
                  }}
                >
                  <Icon name="trash" size={14} />
                </button>
              )}
            </li>
          ))}
        </ol>
        {match.canEdit && others.length > 0 && match.maps.length < 5 && (
          <div className="match-merge">
            <Select
              label={t("matches.merge")}
              value={null}
              placeholder={t("matches.merge")}
              groups={[
                {
                  options: others.filter((o) => o.canEdit && o.maps.length + match.maps.length <= 5).map((o) => ({ value: o.id, label: o.title, hint: o.maps.map((x) => mapName(x.map)).join(", ") })),
                },
              ]}
              onChange={(from) => {
                const other = others.find((o) => o.id === from);
                if (other && window.confirm(t("matches.confirmMerge", { from: other.title, into: match.title }))) merge.mutate(from, { onError: fail });
              }}
            />
            <p className="muted small">{t("matches.mergeHelp")}</p>
          </div>
        )}
      </section>

      <MatchNotes match={match} />

      <section className="card match-section" aria-labelledby="match-players">
        <h2 id="match-players" className="section-title">
          {t("matches.players")}
        </h2>
        <div className="match-player-tables">
          {(["a", "b"] as const).map((side, i) => (
            <PlayersTable key={side} name={match.sides[i]} players={match.players.filter((p) => p.side === side)} ours={side === "a" && match.outcome !== null} />
          ))}
        </div>
      </section>
    </div>
  );
}

/** The match's notes, filtered: a click opens the demo at that moment. */
function MatchNotes({ match }: { match: MatchDetail }) {
  const { t } = useTranslation();
  const notes = useMatchNotes(match.id);
  const list = notes.data ?? [];
  if (notes.isPending || list.length === 0) return null;
  return (
    <section className="card match-section match-notes" aria-labelledby="match-notes">
      <h2 id="match-notes" className="section-title">
        {t("matches.notes.title")} <span className="count-pill">{list.length}</span>
      </h2>
      <p className="muted small">{t("matches.notes.help")}</p>
      <NotesBrowser notes={list} />
    </section>
  );
}

function PlayersTable({ name, players, ours }: { name: string; players: MatchPlayer[]; ours: boolean }) {
  const { t } = useTranslation();
  return (
    <table className="match-players">
      <caption>{name}</caption>
      <thead>
        <tr>
          <th scope="col">{t("demos.viewer.cols.player")}</th>
          <th scope="col" className="num">
            {t("matches.cols.maps")}
          </th>
          <th scope="col" className="num" title={t("demos.viewer.cols.kdaHelp")}>
            {t("demos.viewer.cols.kda")}
          </th>
          <th scope="col" className="num" title={t("matches.cols.diffHelp")}>
            +/−
          </th>
          <th scope="col" className="num">
            {t("demos.viewer.cols.adr")}
          </th>
        </tr>
      </thead>
      <tbody>
        {players.map((p) => (
          <tr key={p.steamId} className={ours && !p.member ? "is-random" : ""}>
            <th scope="row">
              <span className="match-player">
                {p.member ? <Avatar name={p.member.name} url={p.member.avatarUrl} size={22} /> : <span className="match-player-dot" aria-hidden="true" />}
                <span>{p.name}</span>
                {ours && !p.member && <span className="demo-tag">{t("matches.random")}</span>}
              </span>
            </th>
            <td className="num mono">{p.maps}</td>
            <td className="num mono">
              {p.kills}/{p.deaths}/{p.assists}
            </td>
            <td className={`num mono ${p.kills - p.deaths > 0 ? "is-plus" : p.kills - p.deaths < 0 ? "is-minus" : ""}`}>
              {p.kills - p.deaths > 0 ? "+" : ""}
              {p.kills - p.deaths}
            </td>
            <td className="num mono">{p.adr}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MatchEditForm({ match, onDone }: { match: MatchDetail; onDone: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateMatch(match.id);
  const [title, setTitle] = useState(match.title);
  const [source, setSource] = useState<DemoSource>(match.source);
  const [event, setEvent] = useState(match.event ?? "");
  const [stage, setStage] = useState(match.stage ?? "");
  const [bestOf, setBestOf] = useState<BestOf>(match.bestOf);
  const [playedAt, setPlayedAt] = useState(dateInput(match.playedAt));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate(
      { title, source, event: event.trim() || null, stage: stage.trim() || null, bestOf, playedAt: playedAt ? new Date(`${playedAt}T12:00:00`).toISOString() : null },
      { onSuccess: onDone },
    );
  };
  return (
    <form className="card match-edit" onSubmit={submit}>
      <MatchFields
        title={title}
        setTitle={setTitle}
        source={match.pro ? null : source}
        setSource={setSource}
        event={event}
        setEvent={setEvent}
        stage={stage}
        setStage={setStage}
        bestOf={bestOf}
        setBestOf={setBestOf}
        playedAt={playedAt}
        setPlayedAt={setPlayedAt}
      />
      {update.error && <p className="field-error">{errorText(t, update.error)}</p>}
      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={update.isPending || !title.trim()}>
          {t("matches.saveChanges")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onDone}>
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

/** A match's details, in the import page and the match page. */
export function MatchFields(p: {
  title: string;
  setTitle: (v: string) => void;
  /** null: Pro matches, no source to pick. */
  source: DemoSource | null;
  setSource: (v: DemoSource) => void;
  event: string;
  setEvent: (v: string) => void;
  stage: string;
  setStage: (v: string) => void;
  bestOf: BestOf;
  setBestOf: (v: BestOf) => void;
  playedAt: string;
  setPlayedAt: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="match-fields">
      <label className="field match-field-title">
        <span>{t("demos.fields.title")}</span>
        <input value={p.title} maxLength={120} required onChange={(e) => p.setTitle(e.target.value)} />
      </label>
      {p.source !== null && (
        <div className="field">
          <span id="mf-source">{t("demos.fields.source")}</span>
          <div className="segmented segmented-small" role="group" aria-labelledby="mf-source">
            {EDIT_SOURCES.map((s) => (
              <button key={s} type="button" className="seg" aria-pressed={p.source === s} onClick={() => p.setSource(s)}>
                {t(`demos.source.${s}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="field">
        <span id="mf-bo">{t("matches.fields.bestOf")}</span>
        <div className="segmented segmented-small" role="group" aria-labelledby="mf-bo">
          {BEST_OF.map((n) => (
            <button key={n} type="button" className="seg" aria-pressed={p.bestOf === n} onClick={() => p.setBestOf(n)}>
              {t("matches.bestOf", { n })}
            </button>
          ))}
        </div>
      </div>
      <label className="field">
        <span>{t("demos.fields.event")}</span>
        <input value={p.event} maxLength={120} placeholder={t("matches.fields.eventHint")} onChange={(e) => p.setEvent(e.target.value)} />
      </label>
      <label className="field">
        <span>{t("matches.fields.stage")}</span>
        <input value={p.stage} maxLength={80} placeholder={t("matches.fields.stageHint")} onChange={(e) => p.setStage(e.target.value)} />
      </label>
      <label className="field">
        <span>{t("demos.fields.playedAt")}</span>
        <input type="date" value={p.playedAt} onChange={(e) => p.setPlayedAt(e.target.value)} />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------- one map's games

/** The library map's Demos section: that map's games, from the team's matches. */
export function MapGames({ matches, map }: { matches: MatchSummary[]; map: string }) {
  const { t } = useTranslation();
  const date = useDate();
  const games = matches.flatMap((match) => match.maps.filter((g) => g.map === map).map((g) => ({ match, g })));
  return (
    <ul className="map-games">
      {games.map(({ match, g }) => {
        const series = match.bestOf > 1 || match.maps.length > 1;
        const ours = match.outcome !== null && g.winner;
        return (
          <li key={g.demoId} className="card map-game">
            <Link to={`/demos/${g.demoId}`} className="map-game-link" aria-label={t("demos.watchNamed", { title: `${match.title}, ${mapName(map)}` })}>
              <div className="map-game-main">
                <span className="map-game-title">
                  {series && <span className="muted">{t("matches.mapOf", { n: g.number, bo: match.bestOf })} · </span>}
                  <b>{match.title}</b>
                </span>
                <span className="muted small">{[match.playedAt ? date(match.playedAt) : null, match.event, t("demos.rounds", { count: g.rounds })].filter(Boolean).join(" · ")}</span>
              </div>
              <LabelBadge label={match.label} />
              <span className="mono map-game-score">
                {g.score[0]}–{g.score[1]}
              </span>
              {ours && <span className={`match-outcome is-${g.winner === "a" ? "win" : "loss"}`}>{t(g.winner === "a" ? "matches.outcome.win" : "matches.outcome.loss")}</span>}
              <Icon name="play" size={18} />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
